/**
 * Application theme configuration
 * Uses centralized color definitions from colors.ts
 */

import { colors } from './colors';

// Common theme properties shared between light and dark modes
const commonTheme = {
  colors: {
    primary: colors.primary,
    success: colors.success,
    danger: colors.danger,
    warning: colors.warning,
    neutral: colors.neutral,
    info: colors.info,
    purple: colors.purple,
    pink: colors.pink,
    teal: colors.teal,
    white: colors.white,
    black: colors.black,
    transparent: colors.transparent,
  },
  spacing: {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
  },
  borderRadius: {
    none: '0',
    sm: '4px',
    default: '8px',
    base: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    default: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    primary: `0 4px 14px 0 ${colors.primary[500]}40`,
    primaryHover: `0 6px 20px 0 ${colors.primary[500]}50`,
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  breakpoints: {
    sm: '640px',
    md: '834px',
    lg: '1200px',
    xl: '1400px',
    '2xl': '1536px',
  },
} as const;

// Light theme
export const lightTheme = {
  ...commonTheme,
  mode: 'light' as const,
  colors: {
    ...commonTheme.colors,
    // Legacy aliases for backward compatibility
    gray50: colors.neutral[50],
    gray100: colors.neutral[100],
    gray200: colors.neutral[200],
    gray300: colors.neutral[300],
    gray400: colors.neutral[400],
    gray500: colors.neutral[500],
    gray600: colors.neutral[600],
    gray700: colors.neutral[700],
    gray800: colors.neutral[800],
    gray900: colors.neutral[900],
    // Semantic color aliases
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[700],
      muted: colors.neutral[500],
    },
    textSecondary: colors.neutral[700],
    background: {
      primary: colors.neutral[50],
      secondary: colors.neutral[100],
      tertiary: colors.neutral[200],
      card: colors.white,
      overlay: 'rgba(0, 0, 0, 0.5)',
    },
    border: {
      primary: colors.neutral[200],
      secondary: colors.neutral[300],
    },
    error: colors.danger[500],
    // Sidebar (gradient background - white text/icons)
    sidebar: {
      background: '#0f3333',
      text: 'rgba(255, 255, 255, 0.85)',
      textHover: '#ffffff',
      activeBackground: 'rgba(255, 255, 255, 0.12)',
      activeText: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    // TopBar
    topbar: {
      background: '#103433',
      text: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    // Cards
    card: {
      background: colors.white,
      border: colors.neutral[200],
      shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
    },
    // Input
    input: {
      background: colors.white,
      border: colors.neutral[300],
      text: colors.neutral[900],
      placeholder: colors.neutral[400],
    },
    // Chart
    chart: {
      background: colors.white,
    },
  },
};

// Dark theme
export const darkTheme = {
  ...commonTheme,
  mode: 'dark' as const,
  colors: {
    ...commonTheme.colors,
    // Legacy aliases for backward compatibility (inverted for dark mode)
    gray50: colors.neutral[900],
    gray100: colors.neutral[800],
    gray200: colors.neutral[700],
    gray300: colors.neutral[600],
    gray400: colors.neutral[500],
    gray500: colors.neutral[400],
    gray600: colors.neutral[300],
    gray700: colors.neutral[200],
    gray800: colors.neutral[100],
    gray900: colors.neutral[50],
    // Semantic color aliases (inverted)
    text: {
      primary: '#ffffff',
      secondary: colors.neutral[200],
      muted: colors.neutral[400],
    },
    textSecondary: colors.neutral[200],
    background: {
      primary: '#0f1419',
      secondary: '#1a1f26',
      tertiary: '#242a33',
      card: '#1a1f26',
      overlay: 'rgba(0, 0, 0, 0.7)',
    },
    border: {
      primary: colors.neutral[700],
      secondary: colors.neutral[600],
    },
    error: colors.danger[400],
    // Sidebar
    sidebar: {
      background: '#0f3333',
      text: 'rgba(255, 255, 255, 0.85)',
      textHover: '#ffffff',
      activeBackground: 'rgba(255, 255, 255, 0.12)',
      activeText: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    // TopBar
    topbar: {
      background: '#103433',
      text: '#ffffff',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    // Cards
    card: {
      background: '#1a1f26',
      border: colors.neutral[700],
      shadow: '0 1px 3px 0 rgb(0 0 0 / 0.3)',
    },
    // Input
    input: {
      background: '#242a33',
      border: colors.neutral[600],
      text: colors.neutral[50],
      placeholder: colors.neutral[500],
    },
    // Chart
    chart: {
      background: '#1e2530',
    },
  },
  shadows: {
    ...commonTheme.shadows,
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.2)',
    default: '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.3), 0 4px 6px -4px rgb(0 0 0 / 0.3)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3)',
  },
};

// Default theme export (light mode)
export const theme = lightTheme;

export type ThemeMode = 'light' | 'dark';
export type Theme = typeof lightTheme;

// Augment styled-components DefaultTheme - use string for dynamic values
declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme {
    mode: ThemeMode;
    colors: {
      primary: typeof colors.primary;
      success: typeof colors.success;
      danger: typeof colors.danger;
      warning: typeof colors.warning;
      neutral: typeof colors.neutral;
      info: typeof colors.info;
      purple: typeof colors.purple;
      pink: typeof colors.pink;
      teal: typeof colors.teal;
      white: string;
      black: string;
      transparent: string;
      gray50: string;
      gray100: string;
      gray200: string;
      gray300: string;
      gray400: string;
      gray500: string;
      gray600: string;
      gray700: string;
      gray800: string;
      gray900: string;
      text: {
        primary: string;
        secondary: string;
        muted: string;
      };
      textSecondary: string;
      background: {
        primary: string;
        secondary: string;
        tertiary: string;
        card: string;
        overlay: string;
      };
      border: {
        primary: string;
        secondary: string;
      };
      error: string;
      sidebar: {
        background: string;
        text: string;
        textHover: string;
        activeBackground: string;
        activeText: string;
        border: string;
      };
      topbar: {
        background: string;
        text: string;
        border: string;
      };
      card: {
        background: string;
        border: string;
        shadow: string;
      };
      input: {
        background: string;
        border: string;
        text: string;
        placeholder: string;
      };
      chart: {
        background: string;
      };
    };
    spacing: typeof lightTheme.spacing;
    borderRadius: typeof lightTheme.borderRadius;
    shadows: {
      sm: string;
      default: string;
      md: string;
      lg: string;
      xl: string;
      primary: string;
      primaryHover: string;
    };
    fontSize: typeof lightTheme.fontSize;
    fontWeight: typeof lightTheme.fontWeight;
    breakpoints: typeof lightTheme.breakpoints;
  }
}
